import { css } from '@emotion/react';
import { Stack, Tab, Tabs, Typography } from '@mui/material';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useEffect, useMemo } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router';
import { ROUTES } from '../../../constants/RouteNameMapping';
import { ContainerName } from '../../../constants/styles';
import { PermissionKey } from '../../../services/PermissionAndRolesKeys';
import { PermissionService } from '../../../services/PermissionService';
import { MaggieFeatureFlags } from '../../../util/feature-flags';
import { useLoadDummyData } from './DummyData';

const mainContainer = css`
  width: 100%;
`;

const dataDisplayContainer = css`
  container-type: inline-size;
  container-name: ${ContainerName.dataDisplayContainer};
  padding-bottom: 2rem;
`;

const tabContentsContainer = css`
  padding: 8px 12px 12px;
`;

export function DataDisplay() {
  const { showTearsheetLayoutBuilder } = useFlags<MaggieFeatureFlags>();
  const pathname = useLocation().pathname;
  const navigate = useNavigate();
  const selectedTab = useMemo(() => {
    if (showTearsheetLayoutBuilder) {
      if (pathname.includes(ROUTES.TEAR_SHEETS_V1)) {
        return ROUTES.TEAR_SHEETS_V1;
      }
      if (pathname.includes(ROUTES.TEAR_SHEETS_V2)) {
        return ROUTES.TEAR_SHEETS_V2;
      }
    }
    if (pathname.includes(ROUTES.TEAR_SHEETS)) {
      return ROUTES.TEAR_SHEETS;
    }
    if (pathname.includes(ROUTES.COMPANY_LIST)) {
      return ROUTES.COMPANY_LIST;
    }
    if (pathname.includes(ROUTES.KPI_DISPLAY)) {
      return ROUTES.KPI_DISPLAY;
    }
    if (pathname.includes(ROUTES.COMPANY_PROFILES)) {
      return ROUTES.COMPANY_PROFILES;
    }
    return null;
  }, [pathname, showTearsheetLayoutBuilder]);
  const ready = useLoadDummyData();

  useEffect(() => {
    if (selectedTab) {
      return;
    }
    navigate(ROUTES.COMPANY_PROFILES, { replace: true });
  }, [selectedTab, navigate]);

  if (!ready) {
    return null;
  }

  return (
    <div css={mainContainer}>
      <Tabs value={selectedTab} sx={{ position: 'sticky', top: 0, zIndex: 1, backgroundColor: 'white' }}>
        <Tab
          label='Company Summary'
          value={ROUTES.COMPANY_PROFILES}
          to={ROUTES.COMPANY_PROFILES}
          component={Link}
        />
        {PermissionService.get().hasPermission(PermissionKey.canViewMetrics) &&
          PermissionService.get().hasPermission(PermissionKey.canViewKpis) && (
            <Tab label='Tearsheets' value={ROUTES.TEAR_SHEETS} to={ROUTES.TEAR_SHEETS} component={Link} />
          )}
        <Tab label='Company Lists' value={ROUTES.COMPANY_LIST} to={ROUTES.COMPANY_LIST} component={Link} />
        {PermissionService.get().hasPermission(PermissionKey.canViewKpis) && (
          <Tab label='KPI Display' value={ROUTES.KPI_DISPLAY} to={ROUTES.KPI_DISPLAY} component={Link} />
        )}
        {showTearsheetLayoutBuilder && (
          <Tab
            label='Tearsheet Templates'
            value={ROUTES.TEAR_SHEETS_V1}
            to={ROUTES.TEAR_SHEETS_V1}
            component={Link}
          />
        )}
      </Tabs>
      <Typography
        color='text.primary'
        component={Stack}
        height='100%'
        width='100%'
        css={tabContentsContainer}
      >
        <div css={dataDisplayContainer}>
          <Outlet />
        </div>
      </Typography>
    </div>
  );
}
